import { useRef, useState } from 'react';
import { toast } from 'sonner';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import AttentionPanel from '@/components/ui/AttentionPanel.tsx';
import AttentionPanelSkeleton from '@/components/ui/AttentionPanelSkeleton.tsx';
import {
  InfoCard,
  InfoCardBody,
  InfoCardHeader,
} from '@/components/ui/InfoCard.tsx';
import LoadError from '@/components/ui/LoadError.tsx';
import { AttentionItemDto } from '@/types/stats/AttentionItemDto.ts';
import timeUtils from '@/utils/timeUtils.ts';
import { pluralize } from '@/utils/pluralize.ts';
import useGetTourAttention from '@/features/dashboard/tours/hooks/useGetTourAttention.ts';
import useReopenAttentionFindings from '@/features/dashboard/attention/hooks/useReopenAttentionFindings.ts';
import { createReopenGuard } from '@/features/dashboard/attention/hooks/reopenGuard.ts';
import DeferredAttentionList from '@/features/dashboard/attention/components/DeferredAttentionList.tsx';
import ResolveFindingDialog from '@/features/dashboard/attention/components/ResolveFindingDialog.tsx';
import { AttentionLinkScope } from '@/features/dashboard/attention/routes/attentionLinks.ts';
import {
  attentionItemLabel,
  toAttentionPanelItems,
} from '@/features/dashboard/attention/types/AttentionKey.utils.ts';

type PanelView = 'open' | 'deferred';

interface TourAttentionPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  tourId: string;
  title: string;
  /** Gdy podane, panel pokazuje tylko sprawy z tych kluczy — Opcje biorą sprawy o miejscach. */
  keys?: ReadonlySet<string>;
}

/**
 * Panel „Wymaga uwagi” wyjazdu. Ma własne zapytanie i własne stany, bo sprawy
 * przyjeżdżają z innego modułu niż karty obok — błąd spraw nie może zabrać
 * obłożenia ani sald, a skeleton kart nie powinien czekać na sprawy.
 * Przełącznik otwarte/odłożone żyje w stanie komponentu, nie w adresie.
 */
const TourAttentionPanel = ({
  tourId,
  title,
  keys,
  ...props
}: TourAttentionPanelProps) => {
  const { data, isPending, isError } = useGetTourAttention(tourId);
  const [view, setView] = useState<PanelView>('open');
  const [resolving, setResolving] = useState<AttentionItemDto | null>(null);
  const reopen = useReopenAttentionFindings();
  // Toast i menu wołają domknięcia ze starszego renderu, w którym `isPending` jest
  // nieaktualne — drugi klik „Cofnij” / „Przywróć” zatrzymuje dopiero ref.
  const reopening = useRef(createReopenGuard());

  if (isPending) return <AttentionPanelSkeleton {...props} />;
  if (isError || !data)
    return (
      <InfoCard {...props}>
        <InfoCardHeader title={title} />
        <InfoCardBody>
          <LoadError subject="spraw wymagających uwagi" />
        </InfoCardBody>
      </InfoCard>
    );

  const scope: AttentionLinkScope = { kind: 'tour', tourId };
  const byKeys = (items: AttentionItemDto[]) =>
    keys ? items.filter((item) => keys.has(item.key)) : items;
  const open = byKeys(data.items);
  const deferred = byKeys(data.deferred);
  const meta = data.calculatedAt
    ? `stan na ${timeUtils.toDateTimeString(data.calculatedAt)}`
    : undefined;

  // Przywrócenie ostatniej odłożonej opróżnia ten widok — pusty panel nic by już nie pokazał.
  const effectiveView: PanelView =
    view === 'deferred' && deferred.length === 0 ? 'open' : view;

  const reopenAll = async (item: AttentionItemDto) => {
    if (!reopening.current.acquire(item.findingIds)) return;
    try {
      await reopen.mutateAsync({ findingIds: item.findingIds, tourId });
    } catch {
      // Błąd pokazał już toast hooka; tu tylko nie ogłaszamy sukcesu.
      return;
    } finally {
      reopening.current.release(item.findingIds);
    }
    toast.success('Przywrócono', { description: attentionItemLabel(item) });
  };

  const onResolved = (item: AttentionItemDto) => {
    setResolving(null);
    // `view` mogło zostać na „odłożonych” po opróżnieniu tej listy; bez resetu
    // pierwsze odłożenie przerzuciłoby panel z otwartych na odłożone.
    setView('open');
    toast.success('Odłożono', {
      description: attentionItemLabel(item),
      action: { label: 'Cofnij', onClick: () => void reopenAll(item) },
    });
  };

  const headerAction =
    deferred.length > 0 ? (
      <ActionButton
        tone="ghost"
        size="xs"
        aria-pressed={effectiveView === 'deferred'}
        onClick={() => setView(effectiveView === 'open' ? 'deferred' : 'open')}
      >
        {effectiveView === 'open'
          ? pluralize(deferred.length, 'odłożona', 'odłożone', 'odłożonych')
          : pluralize(open.length, 'otwarta', 'otwarte', 'otwartych')}
      </ActionButton>
    ) : undefined;

  return (
    <>
      {effectiveView === 'open' ? (
        <AttentionPanel
          title={title}
          meta={meta}
          headerAction={headerAction}
          items={toAttentionPanelItems(open, {
            scope,
            onResolve: setResolving,
          })}
          {...props}
        />
      ) : (
        <DeferredAttentionList
          title={title}
          meta={meta}
          headerAction={headerAction}
          items={deferred}
          scope={scope}
          onReopen={(item) => void reopenAll(item)}
          {...props}
        />
      )}
      {resolving && (
        <ResolveFindingDialog
          item={resolving}
          label={attentionItemLabel(resolving)}
          tourId={tourId}
          onClose={() => setResolving(null)}
          onResolved={onResolved}
        />
      )}
    </>
  );
};

export default TourAttentionPanel;
